import { decode, getBreakpoint } from 'utils/general';
import select from 'dom-select';
import inViewport from 'in-viewport';
import on, { off } from 'dom-event';
import debounce from 'lodash.debounce';
import { listProductsEventData } from 'lib/tracking';
import datalayer from 'lib/datalayer';
import { A11y, Navigation, Keyboard } from 'swiper/modules';

/**
 * Product card slider swiper component
 */
export default {
  components: {
    'product-card': () => import('../product-card/product-card.vue'),
    'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
  },
  props: {
    products: { default: () => [] },
    dynamicTitle: { type: String, default: '' },
    parentTitle: { type: String, default: '' },
    listType: { type: String, default: '' },
    swiperOptions: {
      a11y: {
        enabled: true,
      },
      keyboard: true,
      autoHeight: true,
      modules: [A11y, Navigation, Keyboard]
    },
    handleResize: { type: Boolean, default: true },
    isSlider: { type: Boolean, default: true },
    showProgressBar: { type: Boolean, default: false },
    showControls: { type: Boolean, default: true },
    disableOnMobile: { type: Boolean, default: false },
    stepKey: { type: Number, default: 0 },
    variants: { type: String, default: '' }
  },
  data() {
    return {
      isViewed: false,
      viewportSize: '',
      progress: 0
    };
  },
  watch: {
    productsDecoded(_new, _old) {
      if (this.isViewed) {
        this.trackView();
      }
    }
  },
  mounted() {
    setTimeout(() => {
      this.resizeHandler();
    }, 250);
    on(window, 'resize', debounce(this.resizeHandler, 300));
    inViewport(this.$el, () => {
      this.isViewed = true;
      this.trackView();
    });

    this.updateSlideAccessibility()
  },
  destroyed() {
    off(window, 'resize', this.resizeHandler);
  },
  computed: {
    productsDecoded() {
      const productsData =
        typeof this.products === 'string'
          ? JSON.parse(decode(this.products))
          : this.products;

      return productsData.map((p, index) => ({ ...p, index: index }));
    },
    variantsDecoded() {
      if (this.variants) {
        return this.variants.split(',')
      }

      return []
    }
  },
  methods: {
    resizeHandler() {
      if (!this.handleResize) return;

      this.viewportSize = getBreakpoint();

      if (this.viewportSize === 'xs' || this.viewportSize === 'xxs') {
        this.adjustVTO();
      }
    },
    adjustVTO() {
      // eslint-disable-next-line no-case-declarations
      const vtoItems = select(
        '.foundation-vto__slider .product-card__featured',
      );
      const vtoPageDots = select('.foundation-vto__slider .flickity-page-dots');
      if (vtoItems && vtoPageDots) {
        const topPos = vtoItems.offsetHeight + 8;
        vtoPageDots.style.top = `${topPos}px`;
      }
    },
    async trackView() {
      if (this.productsDecoded.length < 1) {
        return;
      }

      const extraData = {
        listName: this.parentTitle,
        listId: this.listType,
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
      };

      const { products, hasBundleBuilder } = await listProductsEventData(
        this.productsDecoded,
        extraData,
      );

      let eventData = {
        ecommerce: {
          item_list_id: this.listType || undefined,
          item_list_name: this.parentTitle || undefined,
          items: products,
        },
      };
      if (hasBundleBuilder) {
        eventData = {
          ...eventData,
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder PLP Product Card',
          bundle_type: 'bundle builder',
        };
      }
      // track visible elements on page load
      datalayer.dataLayerPush('view_item_list', eventData);
    },
    onSelectItem(value) {
      this.$emit('select-item', {
        key: Number(this.stepKey),
        value,
      });
    },
    updateSlideAccessibility() {
      if (!this.$refs.carousel) return

      const swiper = this.$refs.carousel.$swiper
      const swiperBounds = swiper.el.getBoundingClientRect();

      swiper.slides.forEach((slide) => {
        const slideBounds = slide.getBoundingClientRect();
        if (
          slideBounds.left < swiperBounds.right &&
          slideBounds.right > swiperBounds.left
        ) {
          slide.setAttribute('aria-hidden', 'false');
          slide.setAttribute('tabindex', '0');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');

          slide.querySelectorAll('*').forEach((child) => {
            child.setAttribute('tabindex', '-1');
          });
        }
      });
    }
  },
};
